import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { UserPlus, ShieldAlert, KeyRound, Copy, Upload, CheckCircle2, XCircle, ScrollText, Mail } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [env, setEnv] = useState(null);
  const [services, setServices] = useState(null);
  const [userDlg, setUserDlg] = useState(false);
  const [userForm, setUserForm] = useState({ email: "", password: "", full_name: "", roles: ["reception"] });
  const [resetDlg, setResetDlg] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [bootstrapDlg, setBootstrapDlg] = useState(false);
  const [bootstrapForm, setBootstrapForm] = useState({ admin_email: "", admin_full_name: "", admin_password: "", deactivate_demo: true });

  const load = async () => {
    const [s, u, r, e, sv] = await Promise.all([
      api.get("/settings"),
      api.get("/users"),
      api.get("/users/roles/list"),
      api.get("/admin/environment").catch(() => ({ data: null })),
      api.get("/dashboard/services-status").catch(() => ({ data: null })),
    ]);
    setSettings(s.data); setUsers(u.data); setRoles(r.data); setEnv(e.data); setServices(sv.data);
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try { await api.patch("/settings", settings); toast.success("Impostazioni salvate"); }
    catch (e) { toast.error("Errore"); }
  };

  const createUser = async () => {
    if (!userForm.email || !userForm.password || !userForm.full_name) return toast.error("Compilare tutti i campi");
    try {
      await api.post("/users", userForm);
      toast.success("Utente creato. Al primo accesso sarà obbligato a cambiare password.");
      setUserDlg(false);
      setUserForm({ email: "", password: "", full_name: "", roles: ["reception"] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); } catch {}
  };

  const openReset = (u) => { setResetDlg({ user: u, tempPassword: null }); setResetPassword(""); };

  const doReset = async (generate) => {
    if (!resetDlg?.user) return;
    try {
      const body = generate ? {} : { new_password: resetPassword };
      const r = await api.post(`/users/${resetDlg.user.id}/reset-password`, body);
      setResetDlg({ ...resetDlg, tempPassword: r.data.temporary_password, generated: r.data.generated });
      toast.success("Password reimpostata. L'utente dovrà cambiarla al prossimo accesso.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const runBootstrap = async () => {
    try {
      const r = await api.post("/admin/bootstrap-production", bootstrapForm);
      toast.success(r.data.message);
      setBootstrapDlg(false);
      setBootstrapForm({ admin_email: "", admin_full_name: "", admin_password: "", deactivate_demo: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const onLogoUpload = async (e, field) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) return toast.error("Il file supera 500 KB — usa un'immagine più piccola.");
    const reader = new FileReader();
    reader.onload = () => setSettings((s) => ({ ...s, [field]: reader.result }));
    reader.readAsDataURL(f);
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="settings-page">
      <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0a0a2e] mb-1">Impostazioni</h1>
      <p className="text-slate-500 mb-6">Configurazione applicazione, branding e utenti.</p>

      {env?.is_demo_mode && (
        <Alert className="mb-4 bg-amber-50 border-amber-200" data-testid="demo-mode-banner">
          <ShieldAlert className="w-4 h-4 text-amber-700" />
          <AlertTitle>Ambiente demo attivo</AlertTitle>
          <AlertDescription>Sono presenti {env.demo_users_active} utenti demo e nessun amministratore di produzione. Prima di andare in produzione utilizza <b>Prepara ambiente di produzione</b> per creare l'amministratore reale e disattivare le credenziali demo.</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="branding">
        <TabsList>
          <TabsTrigger value="branding" data-testid="tab-branding">Branding & azienda</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Utenti e ruoli</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrazioni</TabsTrigger>
          <TabsTrigger value="aliases" data-testid="tab-aliases">Alias marchi</TabsTrigger>
          <TabsTrigger value="diagnostics" data-testid="tab-diagnostics">Diagnostica dispositivo</TabsTrigger>
        </TabsList>

        <TabsContent value="branding" className="pt-4">
          <Card className="ama-card border-0 shadow-none">
            <CardHeader><CardTitle className="font-heading">Identità visiva e loghi ufficiali</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <LogoUpload label="Logo principale (usato in header, PDF, email)" testid="logo-main" value={settings.logo_data_url} onChange={(e) => onLogoUpload(e, "logo_data_url")} onClear={() => setSettings({ ...settings, logo_data_url: "" })} />
                <LogoUpload label="Logo compatto (icona sidebar)" testid="logo-compact" value={settings.logo_compact_data_url} onChange={(e) => onLogoUpload(e, "logo_compact_data_url")} onClear={() => setSettings({ ...settings, logo_compact_data_url: "" })} />
                <LogoUpload label="Favicon" testid="logo-favicon" value={settings.favicon_data_url} onChange={(e) => onLogoUpload(e, "favicon_data_url")} onClear={() => setSettings({ ...settings, favicon_data_url: "" })} />
              </div>
              <div className="text-xs text-slate-500">I loghi caricati vengono usati automaticamente nel login, header dell'applicazione, PDF di accettazione, distinta di spedizione, pagina di autorizzazione cliente e verbale di riconsegna. Se nessun logo è caricato viene mostrato il segno tipografico predefinito AMA.</div>
            </CardContent>
          </Card>

          <Card className="ama-card border-0 shadow-none mt-4">
            <CardHeader><CardTitle className="font-heading">Dati azienda e applicazione</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Nome applicazione</Label><Input value={settings.app_name || ""} onChange={(e) => setSettings({ ...settings, app_name: e.target.value })} /></div>
                <div><Label>Slogan</Label><Input value={settings.slogan || ""} onChange={(e) => setSettings({ ...settings, slogan: e.target.value })} /></div>
                <div><Label>Ragione sociale</Label><Input value={settings.company_name || ""} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} /></div>
                <div><Label>P.IVA</Label><Input value={settings.company_vat || ""} onChange={(e) => setSettings({ ...settings, company_vat: e.target.value })} /></div>
                <div><Label>REA</Label><Input value={settings.company_rea || ""} onChange={(e) => setSettings({ ...settings, company_rea: e.target.value })} /></div>
                <div><Label>Website</Label><Input value={settings.company_website || ""} onChange={(e) => setSettings({ ...settings, company_website: e.target.value })} /></div>
                <div className="md:col-span-2"><Label>Indirizzo</Label><Input value={settings.company_address || ""} onChange={(e) => setSettings({ ...settings, company_address: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input value={settings.company_phone || ""} onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={settings.company_email || ""} onChange={(e) => setSettings({ ...settings, company_email: e.target.value })} /></div>
                <div><Label>Colore primario</Label><Input value={settings.primary_color || ""} onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })} /></div>
                <div><Label>Colore secondario</Label><Input value={settings.secondary_color || ""} onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })} /></div>
              </div>
              <div><Label>Condizioni di accettazione</Label><Textarea rows={5} value={settings.acceptance_terms || ""} onChange={(e) => setSettings({ ...settings, acceptance_terms: e.target.value })} /></div>
              <div><Label>Testo privacy</Label><Textarea rows={3} value={settings.privacy_text || ""} onChange={(e) => setSettings({ ...settings, privacy_text: e.target.value })} /></div>
              <div className="flex justify-end"><Button data-testid="settings-save" className="ama-btn-primary" onClick={saveSettings}>Salva</Button></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="pt-4 space-y-4">
          <Card className="ama-card border-0 shadow-none">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Utenti e ruoli</CardTitle>
              <div className="flex gap-2">
                <Button data-testid="bootstrap-btn" variant="outline" onClick={() => setBootstrapDlg(true)}><ShieldAlert className="w-4 h-4 mr-1" /> Prepara produzione</Button>
                <Button data-testid="add-user-btn" onClick={() => setUserDlg(true)} className="ama-btn-primary rounded-full"><UserPlus className="w-4 h-4 mr-1" /> Nuovo utente</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                {users.map((u) => (
                  <div key={u.id} className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-slate-200" data-testid={`user-row-${u.email}`}>
                    <div className="w-10 h-10 rounded-full bg-fuchsia-50 flex items-center justify-center text-[#d63384] font-heading font-bold">{u.full_name?.[0] || u.email[0]}</div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-semibold text-[#0a0a2e]">{u.full_name} <span className="text-xs text-slate-500 font-normal">· {u.email}</span></div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {u.roles.map((r) => <Badge key={r} variant="outline" className="text-[10px]">{r}</Badge>)}
                        {u.is_demo && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Demo</Badge>}
                        {u.must_change_password && <Badge className="bg-fuchsia-100 text-[#9d174d] border-fuchsia-200 text-[10px]">Cambio password richiesto</Badge>}
                      </div>
                    </div>
                    <Button data-testid={`reset-pw-btn-${u.email}`} variant="outline" size="sm" onClick={() => openReset(u)}><KeyRound className="w-3 h-3 mr-1" /> Reimposta password</Button>
                    <Switch data-testid={`user-active-toggle-${u.email}`} checked={u.active} onCheckedChange={() => toggleActive(u)} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="pt-4 space-y-4">
          <Card className="ama-card border-0 shadow-none" data-testid="resend-status-card">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Mail className="w-4 h-4 text-[#d63384]" /> Servizio email (Resend)</CardTitle></CardHeader>
            <CardContent>
              {services?.email ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <Kv label="Configurato" value={services.email.configured ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Sì</Badge> : <Badge className="bg-amber-100 text-amber-800 border-amber-200">No</Badge>} />
                    <Kv label="Dominio verificato" value={services.email.sender_domain_verified === null ? <span className="text-slate-400">—</span> : (services.email.sender_domain_verified ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Sì</Badge> : <Badge className="bg-amber-100 text-amber-800 border-amber-200">Sandbox</Badge>)} />
                    <Kv label="Mittente" value={<span className="font-mono text-xs">{services.email.sender_email || "-"}</span>} />
                    <Kv label="Provider" value={services.email.provider} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /><div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Ultimo invio riuscito</div></div>
                      {services.email.last_success ? (
                        <div className="text-xs mt-1"><b>{services.email.last_success.subject}</b><br/>{services.email.last_success.to} · {services.email.last_success.at}</div>
                      ) : <div className="text-xs text-slate-400 italic mt-1">Nessun invio registrato in questa sessione.</div>}
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center gap-2"><XCircle className="w-4 h-4 text-rose-600" /><div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Ultimo errore</div></div>
                      {services.email.last_failure ? (
                        <div className="text-xs mt-1"><b>{services.email.last_failure.subject}</b><br/>{services.email.last_failure.to} · <span className="text-rose-700">{services.email.last_failure.error}</span></div>
                      ) : <div className="text-xs text-slate-400 italic mt-1">Nessun errore registrato in questa sessione.</div>}
                    </div>
                  </div>
                  {!services.email.configured && (
                    <Alert className="bg-amber-50 border-amber-200">
                      <AlertTitle>Email non ancora configurata</AlertTitle>
                      <AlertDescription>La chiave <span className="font-mono">RESEND_API_KEY</span> non è impostata. Le comunicazioni vengono registrate come bozza con anteprima, link secure copiabile e apertura WhatsApp click-to-open. La chiave deve essere impostata solo lato server, mai nel frontend o nel codice sorgente.</AlertDescription>
                    </Alert>
                  )}
                  <div className="text-xs text-slate-500 border-t pt-3">
                    <b>Ambito email:</b> le email di autenticazione (recupero password, reset, attivazione, cambio forzato) sono destinate esclusivamente agli operatori interni AMA Sicurezza. Le email operative ai clienti installatori (verbale di accettazione, preventivo, autorizzazione, pronto per ritiro, riconsegna) restano su un flusso separato — i clienti installatori non hanno account applicativi.
                  </div>
                </div>
              ) : <div className="text-sm text-slate-500 italic">Stato non disponibile.</div>}
            </CardContent>
          </Card>

          <Card className="ama-card border-0 shadow-none">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><ScrollText className="w-4 h-4 text-[#d63384]" /> Registro attività</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3">Cronologia immutabile degli eventi di sicurezza (login, cambi password, migrazioni hash, reset amministratore, creazione utenti) e degli eventi operativi (creazione pratiche, cambi stato, spedizioni, autorizzazioni cliente, esportazioni). Ogni evento include utente, data/ora, oggetto e IP quando disponibile. Le password, i token di reset e le chiavi API non vengono mai registrati.</p>
              <Link to="/impostazioni/registro-attivita"><Button data-testid="open-audit-log" className="ama-btn-primary rounded-full"><ScrollText className="w-4 h-4 mr-1" /> Apri registro attività</Button></Link>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aliases" className="pt-4 space-y-4">
          <BrandAliasesPanel />
        </TabsContent>

        <TabsContent value="diagnostics" className="pt-4 space-y-4">
          <Card className="ama-card border-0 shadow-none">
            <CardHeader><CardTitle className="font-heading text-lg">Fotocamera e scanner</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3">Verifica se il browser espone <code className="font-mono">getUserMedia</code>, controlla i permessi, elenca le fotocamere, avvia il live stream e testa la lettura del codice a barre/QR con ZXing. Utile per diagnosticare i problemi di accesso alla fotocamera prima di aprire una segnalazione IT.</p>
              <Link to="/impostazioni/diagnostica-fotocamera"><Button data-testid="open-camera-diagnostics" className="ama-btn-primary rounded-full">Apri diagnostica fotocamera</Button></Link>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create user */}
      <Dialog open={userDlg} onOpenChange={setUserDlg}>
        <DialogContent><DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome completo</Label><Input data-testid="new-user-name" value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="new-user-email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} /></div>
            <div>
              <Label>Password temporanea</Label>
              <Input data-testid="new-user-password" type="text" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} />
              <div className="text-xs text-slate-500 mt-1">Almeno 12 caratteri con MAIUSC+minusc+num+speciale. L'utente sarà obbligato a cambiarla al primo accesso.</div>
            </div>
            <div>
              <Label>Ruolo</Label>
              <Select value={userForm.roles[0]} onValueChange={(v) => setUserForm({ ...userForm, roles: [v] })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setUserDlg(false)}>Annulla</Button><Button data-testid="new-user-save" className="ama-btn-primary" onClick={createUser}>Crea</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin reset password */}
      <Dialog open={!!resetDlg} onOpenChange={(v) => !v && setResetDlg(null)}>
        <DialogContent data-testid="admin-reset-dialog">
          <DialogHeader><DialogTitle>Reimposta password — {resetDlg?.user?.email}</DialogTitle></DialogHeader>
          {resetDlg?.tempPassword ? (
            <div className="space-y-3">
              <Alert className="bg-emerald-50 border-emerald-200">
                <AlertTitle>Password temporanea generata</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 font-mono text-lg bg-white border border-emerald-200 rounded-lg px-3 py-2 flex items-center justify-between" data-testid="temp-password-display">
                    <span>{resetDlg.tempPassword}</span>
                    <button onClick={() => { navigator.clipboard.writeText(resetDlg.tempPassword); toast.success("Copiata"); }} className="text-emerald-700 hover:text-emerald-900"><Copy className="w-4 h-4" /></button>
                  </div>
                  <div className="text-xs mt-2">Comunicala all'utente in modo sicuro. Sarà mostrata <b>una sola volta</b> e non è più recuperabile. L'utente dovrà cambiarla al prossimo accesso.</div>
                </AlertDescription>
              </Alert>
              <div className="flex justify-end"><Button className="ama-btn-primary" onClick={() => setResetDlg(null)}>Ho annotato la password</Button></div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Scegli come reimpostare la password. In entrambi i casi l'utente sarà obbligato a cambiarla al prossimo accesso e tutte le sessioni attive saranno invalidate.</p>
              <div className="space-y-2">
                <Label>Genera automaticamente</Label>
                <Button data-testid="reset-generate-btn" variant="outline" className="w-full" onClick={() => doReset(true)}>Genera password sicura</Button>
              </div>
              <div className="space-y-2">
                <Label>Oppure inserisci manualmente</Label>
                <Input data-testid="reset-manual-input" type="text" placeholder="Almeno 12 caratteri con MAIUSC+minusc+num+speciale" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} />
                <Button data-testid="reset-manual-btn" className="ama-btn-primary w-full" disabled={!resetPassword} onClick={() => doReset(false)}>Imposta password inserita</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bootstrap production */}
      <Dialog open={bootstrapDlg} onOpenChange={setBootstrapDlg}>
        <DialogContent data-testid="bootstrap-dialog">
          <DialogHeader><DialogTitle>Prepara ambiente di produzione</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Alert>
              <ShieldAlert className="w-4 h-4" />
              <AlertDescription>Questa azione crea il primo amministratore di produzione e disattiva tutti gli utenti demo. Il nuovo amministratore dovrà cambiare la password al primo accesso.</AlertDescription>
            </Alert>
            <div><Label>Email amministratore</Label><Input data-testid="bootstrap-email" value={bootstrapForm.admin_email} onChange={(e) => setBootstrapForm({ ...bootstrapForm, admin_email: e.target.value })} /></div>
            <div><Label>Nome e cognome</Label><Input data-testid="bootstrap-name" value={bootstrapForm.admin_full_name} onChange={(e) => setBootstrapForm({ ...bootstrapForm, admin_full_name: e.target.value })} /></div>
            <div><Label>Password temporanea</Label>
              <Input data-testid="bootstrap-password" type="text" value={bootstrapForm.admin_password} onChange={(e) => setBootstrapForm({ ...bootstrapForm, admin_password: e.target.value })} />
              <div className="text-xs text-slate-500 mt-1">Almeno 12 caratteri con MAIUSC+minusc+num+speciale. Sarà da cambiare al primo accesso.</div>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setBootstrapDlg(false)}>Annulla</Button><Button data-testid="bootstrap-submit" className="ama-btn-primary" onClick={runBootstrap}>Crea amministratore e disattiva demo</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-[#0a0a2e] font-medium">{value ?? "-"}</div>
    </div>
  );
}

function LogoUpload({ label, testid, value, onChange, onClear }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-4">
      <div className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-2">{label}</div>
      <div className="flex items-center gap-3">
        <div className="w-24 h-24 bg-white rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
          {value ? <img src={value} alt="logo" data-testid={`${testid}-preview`} className="max-w-full max-h-full object-contain" /> : <div className="text-xs text-slate-400 italic">Nessun logo</div>}
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <label className="cursor-pointer">
            <input data-testid={`${testid}-input`} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={onChange} />
            <span className="inline-flex items-center justify-center rounded-full bg-white border border-slate-200 hover:border-[#d63384] transition px-3 py-1.5 text-sm text-[#0a0a2e]"><Upload className="w-3.5 h-3.5 mr-1" /> Carica</span>
          </label>
          {value && <button type="button" onClick={onClear} data-testid={`${testid}-clear`} className="text-xs text-rose-600 hover:text-rose-800 text-left">Rimuovi</button>}
          <div className="text-[10px] text-slate-500">PNG/JPG/SVG · max 500 KB</div>
        </div>
      </div>
    </div>
  );
}

function BrandAliasesPanel() {
  const [items, setItems] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [form, setForm] = useState({ source_brand_name: "", manufacturer_id: "" });
  const load = async () => {
    try {
      const [a, m] = await Promise.all([api.get("/brand-aliases"), api.get("/manufacturers")]);
      setItems(a.data); setManufacturers(m.data);
    } catch { toast.error("Errore caricamento alias"); }
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.source_brand_name.trim() || !form.manufacturer_id) return toast.error("Compila entrambi i campi");
    try { await api.post("/brand-aliases", form); toast.success("Alias creato"); setForm({ source_brand_name: "", manufacturer_id: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare l'alias?")) return; await api.delete(`/brand-aliases/${id}`); load(); };
  return (
    <Card className="ama-card border-0 shadow-none" data-testid="brand-aliases-panel">
      <CardHeader>
        <CardTitle className="font-heading text-lg">Alias marchi</CardTitle>
        <div className="text-sm text-slate-500">Associa varianti di scrittura (es. &laquo;HIK VISION&raquo;, &laquo;Hikvision&raquo;) al produttore corretto in AMA ServiceFlow. Gli alias vengono usati durante l'importazione dal sito e da file.</div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input data-testid="alias-source" placeholder="Marchio come appare sul sito (es. HIK VISION)" value={form.source_brand_name} onChange={(e) => setForm({ ...form, source_brand_name: e.target.value })} />
          <select data-testid="alias-manufacturer" value={form.manufacturer_id} onChange={(e) => setForm({ ...form, manufacturer_id: e.target.value })} className="border border-slate-200 rounded-md px-3 py-2 text-sm">
            <option value="">Produttore AMA ServiceFlow…</option>
            {manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <Button data-testid="alias-add" onClick={create} className="ama-btn-primary">Crea alias</Button>
        </div>
        <div className="grid gap-1">
          {items.length === 0 && <div className="text-sm text-slate-500 italic">Nessun alias configurato.</div>}
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-2 p-2 rounded-md border border-slate-100 bg-white text-sm" data-testid={`alias-row-${a.id}`}>
              <div className="flex-1 font-mono text-xs">{a.source_brand_name}</div>
              <div className="flex-1 text-slate-600">→ {a.manufacturer_name}</div>
              <Button size="sm" variant="outline" onClick={() => del(a.id)} className="text-rose-600 border-rose-200 hover:bg-rose-50">Elimina</Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
